import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AdAccount } from '../../api/ads/ad-account.api';
import { getBrandCampaignStats } from '../../api/payment/brand-deal.api';
import {
  BrandBooking,
  BrandLiveDealSession,
  BrandLiveRateCard,
  getBrandLiveRateCard,
  listBrandLiveDealSessions,
  listBusinessBrandBookings,
  reportBrandLiveSession,
  requestBrandLiveCancel,
} from '../../api/payment/brand-live.api';
import {
  BUSINESS_CANCEL_REASON_MAX,
  BUSINESS_CANCEL_REASON_MIN,
  bookingPartStatusLabel,
  hoursLabel,
  liveStopReasonLabel,
  reportDeadlineLabel,
  reportStatusLabel,
} from '../../utils/brand-live';
import BrandLiveReportDialog, { BrandLiveReasonDialog } from './brand-live-report-dialog';
import BrandLiveReportList from './brand-live-report-list';
import BrandBookingDialog from './brand-booking-dialog';
import { useOrdererSanction } from '../brand-sanction/orderer-sanction-context';
import { SANCTION_LOCK_HINT } from '../../utils/brand-sanction';
import { BrandThreadToggle } from '../brand-deal/brand-thread-panel';

// Mục "Đơn đặt quảng cáo" của doanh nghiệp: trạng thái, giờ đã chạy trên giờ đã mua, buổi live kèm link,
// lượt quét QR (lượt bấm của chiến dịch đặt) và nút "Đặt lại".

const formatVnd = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')}đ`;
const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('vi-VN') : '');

const BookingRow: React.FC<{ b: BrandBooking; onRebook: (b: BrandBooking) => void; onChanged: () => void }> = ({ b, onRebook, onChanged }) => {
  const [clicks, setClicks] = useState<number | null>(null);
  const { blocked } = useOrdererSanction();
  const [sessions, setSessions] = useState<BrandLiveDealSession[] | null>(null);
  const [open, setOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<BrandLiveDealSession | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    getBrandCampaignStats(b.campaignId)
      .then((s) => setClicks(s.totals.clicks))
      .catch(() => setClicks(null));
  }, [b.campaignId]);

  const reloadSessions = () => {
    if (!b.live) return;
    listBrandLiveDealSessions(b.live.dealId)
      .then(setSessions)
      .catch(() => setSessions([]));
  };

  // Còn xin huỷ được: deal đang chạy, chưa dừng, chưa có yêu cầu nào chờ.
  const canCancelLive = !!b.live && b.live.status === 'Airing' && !b.live.stoppedAt && !b.live.pendingCancel;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && b.live && sessions === null) {
      listBrandLiveDealSessions(b.live.dealId)
        .then(setSessions)
        .catch(() => setSessions([]));
    }
  };

  return (
    <div className="rounded-lg border ui-divider p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium ui-text">{b.creatorName}</div>
          <div className="truncate text-xs ui-muted">{b.destinationUrl}</div>
          <div className="mt-1 space-y-0.5 text-sm ui-text">
            {b.post && (
              <div>
                Bài đăng: {bookingPartStatusLabel(b.post.status)} · {formatVnd(b.post.priceVnd)}
              </div>
            )}
            {b.live && (
              <div>
                Livestream: {bookingPartStatusLabel(b.live.status)} · đã chạy {hoursLabel(b.live.shownSeconds)} trên{' '}
                {hoursLabel(b.live.purchasedSeconds)} · {formatVnd(b.live.priceVnd)}
                {b.live.stopReason ? ` · ${liveStopReasonLabel(b.live.stopReason)}` : ''}
                {b.live.pendingCancel
                  ? b.live.cancelReasonCode === 'BrandScandal'
                    ? ' · Creator đã xin dừng, đang chờ admin xét.'
                    : ' · Yêu cầu huỷ của bạn đang chờ admin xét.'
                  : ''}
              </div>
            )}
            <div className="text-xs ui-muted">
              Tổng {formatVnd(b.totalVnd)} · đặt lúc {fmtTime(b.createdAt)}
              {clicks !== null ? ` · ${clicks.toLocaleString('vi-VN')} lượt quét QR` : ''}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          {b.live && (
            <button onClick={toggle} className="rounded-md border ui-divider px-3 py-1.5 text-sm ui-text">
              {open ? 'Ẩn buổi live' : 'Buổi live'}
            </button>
          )}
          {canCancelLive && (
            <button onClick={() => setCancelOpen(true)} className="rounded-md border border-red-500 px-3 py-1.5 text-sm text-red-500">
              Xin huỷ
            </button>
          )}
          <button
            disabled={blocked}
            title={blocked ? SANCTION_LOCK_HINT : undefined}
            onClick={() => onRebook(b)}
            className="rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Đặt lại
          </button>
        </div>
      </div>
      <div className="mt-2">
        <BrandThreadToggle threadId={b.threadId} />
      </div>
      {open && b.live && (
        <div className="mt-2 space-y-1 border-t ui-divider pt-2 text-sm ui-text">
          {sessions === null ? (
            <p className="ui-muted">Đang tải…</p>
          ) : sessions.length === 0 ? (
            <p className="ui-muted">Chưa có buổi live nào.</p>
          ) : (
            sessions.map((s) => (
              <div key={s.sessionId} className="flex flex-wrap gap-x-3">
                <span>{fmtTime(s.startedAt)}</span>
                <span>{hoursLabel(s.seconds)}</span>
                {s.liveUrl ? (
                  <a href={s.liveUrl} target="_blank" rel="noopener noreferrer" className="text-[#0057FF] hover:underline">
                    Xem buổi live
                  </a>
                ) : (
                  <span className="ui-muted">{s.linkMissing ? 'Chưa dán link' : 'Chưa có link'}</span>
                )}
                {s.reportStatus && <span className="font-medium">{reportStatusLabel(s.reportStatus)}</span>}
                {s.canReport && (
                  <>
                    {s.reportDeadline && <span className="ui-muted">{reportDeadlineLabel(s.reportDeadline, 0)}</span>}
                    <button onClick={() => setReportTarget(s)} className="text-red-500 hover:underline">
                      Báo gian lận
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}
      {reportTarget && b.live && (
        <BrandLiveReportDialog
          onSubmit={(reason) => reportBrandLiveSession(b.live!.dealId, reportTarget.sessionId, reason)}
          onClose={() => setReportTarget(null)}
          onDone={() => {
            setReportTarget(null);
            reloadSessions();
            onChanged();
          }}
        />
      )}
      {cancelOpen && b.live && (
        <BrandLiveReasonDialog
          title="Xin huỷ quảng cáo livestream"
          hint="Admin sẽ xem xét. Nếu đồng ý, quảng cáo dừng và creator vẫn được trả theo các buổi hợp lệ."
          placeholder="Lý do xin huỷ"
          submitLabel="Gửi yêu cầu"
          min={BUSINESS_CANCEL_REASON_MIN}
          max={BUSINESS_CANCEL_REASON_MAX}
          failMessage="Không gửi được yêu cầu huỷ."
          onSubmit={async (t) => {
            await requestBrandLiveCancel(b.live!.dealId, t);
            return 'Đã gửi yêu cầu huỷ. Admin sẽ xem xét.';
          }}
          onClose={() => setCancelOpen(false)}
          onDone={() => {
            setCancelOpen(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

const BrandBookingList: React.FC<{ bizAccounts: AdAccount[]; onGoToRatingsTab?: () => void }> = ({
  bizAccounts,
  onGoToRatingsTab,
}) => {
  const [bookings, setBookings] = useState<BrandBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [reportsKey, setReportsKey] = useState(0);
  const [rebook, setRebook] = useState<{ booking: BrandBooking; card: BrandLiveRateCard } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setReportsKey((k) => k + 1);
    listBusinessBrandBookings()
      .then(setBookings)
      .catch(() => toast.error('Không tải được danh sách đơn đặt quảng cáo.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startRebook = async (b: BrandBooking) => {
    try {
      const card = await getBrandLiveRateCard(b.creatorId);
      if (!card) {
        toast.error('Creator chưa nhận quảng cáo livestream.');
        return;
      }
      setRebook({ booking: b, card });
    } catch (e) {
      const r = (e as { response?: { status?: number; data?: { message?: string } } })?.response;
      toast.error(
        r?.status === 404
          ? 'Creator chưa nhận quảng cáo livestream.'
          : r?.data?.message || 'Không tải được bảng giá, thử lại sau.'
      );
    }
  };

  return (
    <div className="space-y-2">
      {loading ? (
        <p className="text-sm ui-muted">Đang tải…</p>
      ) : bookings.length === 0 ? (
        <p className="text-sm ui-muted">Chưa có đơn đặt quảng cáo nào.</p>
      ) : (
        bookings.map((b) => <BookingRow key={b.campaignId} b={b} onRebook={startRebook} onChanged={load} />)
      )}
      <div className="space-y-2 pt-3">
        <div className="text-sm font-semibold ui-text">Báo cáo gian lận</div>
        <BrandLiveReportList role="business" refreshKey={reportsKey} />
      </div>
      {rebook && (
        <BrandBookingDialog
          creatorId={rebook.booking.creatorId}
          creatorName={rebook.booking.creatorName}
          rateCard={rebook.card}
          bizAccounts={bizAccounts}
          initial={rebook.booking}
          onClose={() => setRebook(null)}
          onCreated={() => {
            setRebook(null);
            load();
          }}
          onGoToRatingsTab={onGoToRatingsTab}
        />
      )}
    </div>
  );
};

export default BrandBookingList;
